// @flow
import React, { useEffect, useState } from "react";
import { Button, Container, Divider, Form, Grid, Header, Icon, List, Segment } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { change as changeAction, Field, type FormProps, formValueSelector, getFormValues, reduxForm } from "redux-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { CompanyCheckBox, FooterNote } from "modules/ChangeService/Wizard/misc/Components";
import { AddressForm } from "modules/ChangeService/ChangeServices/submodules/Old_Edit/components/Customer/DeliveryAddress";
import { FormLabelWithDivider as FormLabel } from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import AdditionalInformation from "modules/ChangeService/ChangeServices/submodules/Old_Edit/components/Customer/AdditionalInformation";
import { IconOptions } from "../../../misc/components/IconOptions";
import { Method } from "modules/ChangeService/ChangeServices/submodules/Old_Edit/components/Payment";
import { getStepRoute } from "modules/ChangeService/Wizard/misc/Routes";
import {
  DIFFERENT_ACCOUNT_OWNER,
  DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER,
  DIRECT_DEBIT_IBAN,
  IMPORT_FROM_DELIVERY_ADDRESS,
} from "Commons/Components/FormComponents/components/PaymentForm/Constants";
import PromotionCodeChecker from "Commons/Modules/CouponCodeBox/PromotionCodeChecker";
import { fetchCategoryBySlug } from "redux/actions/category";

import type { CategoryT } from "Commons/Modules/category";
import type { PathT, StepT } from "modules/ChangeService/Wizard/misc/Types";
import "./Customer.scss";
import { COMPANY } from "Commons/Utilities/Constants/PropertyTypes";
import { getPreviousNextStep } from "modules/ChangeService/Wizard/misc/PathHelpers";
import { resetState } from "modules/ChangeService/redux/actions";
import { UtmParams } from "Commons/Utilities/Tracking";
import { loadCustomTrackingData } from "Commons/Utilities/Tracking/TrackingHandler";
import { specialPartnersUtmSource } from "modules/ChangeService/misc/Constants";
import { FormRow } from "modules/ChangeService/Wizard/Desktop/components/FormRow";
import Bonus from "modules/ChangeService/Wizard/misc/components/Bonus/Bonus";
import { useIban } from "modules/MyProfile/components/BankDetails/ibanHooks";
import RadioButtonOptions from "../../../misc/components/RadioButtonOptions";
import Styles from "./Customer.module.scss";

type Props = {|
  onNavigation: Function,
  steps: PathT,
  step: StepT,
  authenticated: boolean,
  categorySlug: string,
  category: CategoryT,
|} & FormProps;

function CustomertStep(props: Props) {
  const {
    onNavigation,
    steps,
    step,
    authenticated,
    categorySlug,
    category,
    change,
    form,
    initialValues,
    location,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation("changeService");
  const { previousStep, nextStep } = getPreviousNextStep(steps, step.id);
  const user = useSelector(({ user }) => user.user);
  const { creationSuccess } = useSelector(({ changeServices }) => changeServices);

  useEffect(() => {
    if (categorySlug) {
      dispatch(fetchCategoryBySlug(categorySlug));
    }
  }, [categorySlug, dispatch]);

  useEffect(() => {
    if (category) {
      change("categoryId", category.id);
    }
  }, [category, change]);

  const [submitsWithErrorsCounter, setSubmitsWithErrorsCounter] = useState(0);
  useEffect(() => {
    let err = document.getElementsByClassName("field error")[0];
    err &&
      err.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
  }, [submitsWithErrorsCounter]);

  const currentValues = useSelector((state) => getFormValues("ChangeService")(state));

  const categoryName = categorySlug === "gas" ? "Gas" : "Strom";

  const isCompany = currentValues.propertyType === COMPANY;
  const [checked, setChecked] = useState(!isCompany);

  const utmSource = loadCustomTrackingData(UtmParams.UTM_SOURCE_CC);

  const billingAddressSameAsDeliveryAddress = "billingAddressSameAsDeliveryAddress";
  const accountOwnerType = "accountOwnerType";

  const selector = formValueSelector("ChangeService");
  const accountNumber = useSelector((state) => selector(state, "accountNumber"));
  const accountBankNumber = useSelector((state) => selector(state, "accountBankNumber"));
  const ibanFromForm = useSelector((state) => selector(state, "accountIban"));

  const desiredPaymentMethod = useSelector((state) => selector(state, "desiredPaymentMethod"));

  const [iban, isIbanValid, isAccountBankNumberValid, isAccountNumberValid] = useIban(
    desiredPaymentMethod,
    ibanFromForm,
    accountNumber,
    accountBankNumber
  );

  useEffect(() => {
    if (desiredPaymentMethod === DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER && iban) {
      dispatch(changeAction("ChangeService", "accountIban", iban));
    }
  }, [iban, desiredPaymentMethod, dispatch]);

  return (
    <Form className="Customer-Mobile">
      <Grid stackable>
        <Grid.Column>
          <Form.Group>
            <FormLabel className={Styles.noPadding} centered width={16} title={t("mobile.customer.title")} />
            <FormLabel
              centered
              width={16}
              title={t("mobile.customer.deliveryAddress", { categoryName })}
              inline
              tooltip={t("mobile.customer.deliveryAddressInfo", { categoryName })}
              secondary
              className={Styles.questionProvider}
            />
          </Form.Group>
          <AddressForm
            initialValues={{ ...initialValues, category: category.categoryType }}
            currentValues={currentValues}
            t={t}
            fields={{
              salutation: "salutation",
              salutationTitle: "salutationTitle",
              firstName: "firstname",
              lastName: "lastname",
              companyName: "companyName",
              taxId: "taxId",
              street: "street",
              streetNumber: "streetNumber",
              postalCode: "postalcode",
              locationId: "locationId",
              locationMainName: "locationMainName",
              locationName: "location",
              locationPartName: "locationPartName",
            }}
            change={change}
            dataCy="deliveryAddress"
            form={form}
            mobileChangeService
          />
          <Divider hidden />
          <AdditionalInformation divider hideSaveButton mobileChangeService />
          <Form.Group>
            <FormLabel
              width={16}
              title={t("mobile.customer.billingAddressSameAsDeliveryAddress.title")}
              centered
              inline
              tooltip={t("mobile.customer.billingAddressSameAsDeliveryAddress.info")}
              secondary
              id={`${billingAddressSameAsDeliveryAddress}Label`}
            />
            <RadioButtonOptions
              ariaLabelledby={`${billingAddressSameAsDeliveryAddress}Label`}
              name={billingAddressSameAsDeliveryAddress}
              normalize={(value) => value === "yes"}
              format={(value) => (value ? "yes" : "no")}
              props={{ name: billingAddressSameAsDeliveryAddress }}
              className={billingAddressSameAsDeliveryAddress}
            />
          </Form.Group>
          {!currentValues.billingAddressSameAsDeliveryAddress && (
            <AddressForm
              initialValues={{ ...initialValues, category: category.categoryType }}
              currentValues={currentValues}
              t={t}
              fields={{
                salutation: "differentSalutation",
                salutationTitle: "differentSalutationTitle",
                firstName: "differentFirstname",
                lastName: "differentLastname",
                companyName: "differentCompanyName",
                street: "differentStreet",
                streetNumber: "differentStreetNumber",
                postalCode: "differentPostalcode",
                locationId: "differentLocationId",
                locationMainName: "differentLocationMainName",
                locationName: "differentLocation",
                locationPartName: "differentLocationPartName",
              }}
              change={change}
              dataCy="differentBillingAddress"
              form={form}
              mobileChangeService
            />
          )}
        </Grid.Column>
      </Grid>
      <Grid className={"note"}>
        <Grid.Column>
          <Header as="h3" className={"title"}>
            {t("mobile.customer.note.title")}
          </Header>
          <List>
            {t("mobile.customer.note.items", { returnObjects: true, categoryName }).map((item, i) => {
              return (
                <List.Item key={i}>
                  <List.Icon name="check circle" className={"icon"} />
                  <List.Content>{item}</List.Content>
                </List.Item>
              );
            })}
          </List>
        </Grid.Column>
      </Grid>
      <Grid stackable>
        <Grid.Column>
          <Form.Group>
            <FormLabel
              width={16}
              title={t("mobile.customer.bank.title")}
              centered
              inline
              tooltip={t("mobile.customer.bank.info")}
              secondary
            />
            <Field
              name="desiredPaymentMethod"
              component={IconOptions}
              props={{
                options: [
                  {
                    icon: "bank",
                    label: t("mobile.customer.bank.options.IBAN"),
                    value: DIRECT_DEBIT_IBAN,
                  },
                  {
                    icon: "company",
                    label: t("mobile.customer.bank.options.BLZ_ACCOUNT_NUMBER"),
                    value: DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER,
                  },
                ],
                onChange: () => {
                  change("accountIban", null);
                  change("accountBankNumber", null);
                  change("accountNumber", null);
                },
              }}
            />
          </Form.Group>
          <Method t={t} validation={{ isIbanValid, isAccountBankNumberValid, isAccountNumberValid }} />
          {specialPartnersUtmSource.includes(utmSource) && (
            <FormRow>
              <Segment color="yellow" className={Styles.bonus}>
                <Bonus type={utmSource} showErrorMessage />
              </Segment>
            </FormRow>
          )}
          <Form.Group>
            <FormLabel
              width={16}
              title={t("mobile.customer.accountOwnerType.title")}
              centered
              inline
              tooltip={t("mobile.customer.accountOwnerType.info")}
              secondary
              id={`${accountOwnerType}Label`}
            />
            <RadioButtonOptions
              ariaLabelledby={`${accountOwnerType}Label`}
              name={accountOwnerType}
              normalize={(value) => (value === "yes" ? IMPORT_FROM_DELIVERY_ADDRESS : DIFFERENT_ACCOUNT_OWNER)}
              format={(value) => (value === IMPORT_FROM_DELIVERY_ADDRESS ? "yes" : "no")}
              props={{ name: accountOwnerType }}
              className={accountOwnerType}
            />
          </Form.Group>
          {currentValues.accountOwnerType === DIFFERENT_ACCOUNT_OWNER && (
            <>
              <AddressForm
                initialValues={{ ...initialValues, category: category.categoryType }}
                currentValues={currentValues}
                t={t}
                fields={{
                  salutation: "differentAccountHolderSalutation",
                  salutationTitle: "differentAccountHolderSalutationTitle",
                  firstName: "differentAccountHolderFirstname",
                  lastName: "differentAccountHolderLastname",
                  companyName: "differentAccountHolderCompanyName",
                  taxId: "differentAccountHolderTaxId",
                  street: "differentAccountHolderStreet",
                  streetNumber: "differentAccountHolderStreetNumber",
                  postalCode: "differentAccountHolderPostalcode",
                  locationId: "differentAccountHolderLocationId",
                  locationMainName: "differentAccountHolderLocationMainName",
                  locationName: "differentAccountHolderLocation",
                  locationPartName: "differentAccountHolderLocationPartName",
                }}
                change={change}
                dataCy="differentAccountOwner"
                form={form}
                mobileChangeService
              />
            </>
          )}
          <PromotionCodeChecker mobileChangeService />
          <Divider hidden />
          <p>
            <strong>{t("mobile.customer.footer.title")}</strong>
          </p>
          {<FooterNote />}
          <Divider hidden />
          <CompanyCheckBox checked={checked} setChecked={setChecked} isCompany={isCompany} />
          <Divider hidden />
          <Container textAlign="center">
            <Link
              to={{
                pathname: getStepRoute(authenticated, categorySlug, nextStep.route),
                search: location.search,
              }}
            >
              <Button
                primary
                disabled={!checked}
                size="big"
                onClick={(event) => {
                  const isValid = onNavigation(event);
                  if (creationSuccess) dispatch(resetState());

                  if (isValid) {
                    setSubmitsWithErrorsCounter(submitsWithErrorsCounter + 1);
                  }
                }}
              >
                {user?.id ? t("wizard.customer.submitButton") : t("common:next")}
                <Icon name="arrow right" />
              </Button>
            </Link>
            <Divider hidden />
            <p>
              <Link
                to={{
                  pathname: getStepRoute(authenticated, categorySlug, previousStep.route),
                  search: location.search,
                }}
              >{`< ${t("common:back")}`}</Link>
            </p>
          </Container>
        </Grid.Column>
      </Grid>
    </Form>
  );
}

const Customer = reduxForm({
  form: "ChangeService",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(CustomertStep);

export { Customer };
